import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setCount(Math.floor(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  return (
    <span ref={ref} className="font-display text-4xl sm:text-5xl lg:text-6xl text-gradient-fire font-medium">
      {count.toLocaleString()}{suffix}
    </span>
  );
};

export const Stats = () => {
  const { t } = useLang();
  return (
    <section data-testid="stats-section" className="py-20 sm:py-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#e8e5e0] border border-[#e8e5e0] rounded-xl overflow-hidden">
          {t.stats.items.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-white p-8 sm:p-10"
            >
              <Counter value={s.value} suffix={s.suffix} />
              <p className="mt-3 font-tech text-[11px] sm:text-xs tracking-[0.18em] uppercase text-neutral-400">{s.label}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          {t.stats.badges.map((b, i) => (
            <span key={i} className="inline-flex items-center gap-2 rounded-full border border-[#e8e5e0] bg-white px-4 py-2 font-tech text-[11px] tracking-widest uppercase text-neutral-500">
              <BadgeCheck size={13} className="text-[#ff6b00]" /> {b}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
